import {useState, useEffect, useRef} from 'react';

import {
    type InboxChannelEventFragment as ChannelEvent,
    ThreadType,
    type GetSessionUser_loggedInUser_sendableEmailAddresses as UserSendableEmailAddress,
} from 'nutshell-graphql-types';

import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import type {CantSendStatusEnum, Recipient} from '../types';

import {ChannelEvents} from './channel-events';
import {getCantSendMessage, getShouldHideControls} from './message-input/helpers';
import {MessageInput} from './message-input/message-input';
import type {SenderAddress} from './message-input/types';

import styles from './channel-container.module.css';

type Props = {
    onSend: (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
        fromAddress?: SenderAddress;
    }) => Promise<any>;
    isLoadingChannelEvents: boolean;
    recipient: Recipient;
    isSendComplete: boolean;
    isSendFailed: boolean;
    channelEvents: ChannelEvent[];
    onResendMessage: (messageId: string) => Promise<any>;
    onDeleteMessage: (messageId: string) => Promise<any>;
    cantSendStatus: CantSendStatusEnum | null | undefined;
    fetchMostRecentChannelEvents: () => Promise<any>;
    refetchScheduledChannelEvents: () => Promise<any>;
    isStartingNewThread?: boolean;
    fetchMoreRows: () => any;
    hasNextPage: boolean;
    showProgress?: boolean;
    showSnoozeOptions?: boolean;
    sessionId: string;
    threadId?: string;
    threadType?: ThreadType;
    sendErrorMessage?: string | null;
    onNoteCreate?: (note: string) => Promise<any>;
    signature?: string | null | undefined;
    threadSummary?: string | null;
    shouldScrollToBottomOnInitialLoad?: boolean;
    isSidebar?: boolean;
    userSendableEmailAddresses?: UserSendableEmailAddress[] | null | undefined;
    shouldHideMessageInput?: boolean;
};

export function ChannelContainer(props: Props) {
    const {isSendComplete, isSendFailed} = props;
    const [isSending, setIsSending] = useState(false);
    const [eventsContainerHeight, setEventsContainerHeight] = useState<any>(undefined);
    const [inputHeight, setInputHeight] = useState<any>(undefined);
    const [containerHeight, setContainerHeight] = useState<any>(undefined);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const handleSend = (input: {
        content: string;
        snoozeDuration?: number;
        closeThread?: boolean;
        imageUrls?: string[];
        fromAddress?: SenderAddress;
    }) => {
        setIsSending(true);

        // Always clear the sending state after 5 seconds
        setTimeout(() => {
            setIsSending(false);
        }, 5000);

        return props.onSend(input).then(() => {
            // ensure we get the thread closed event if it was a send and close deal
            props.fetchMostRecentChannelEvents();
        });
    };

    const handleNoteCreate = async (note: string) => {
        setIsSending(true);
        try {
            await props.onNoteCreate?.(note)?.then(() => {
                props.fetchMostRecentChannelEvents();
            });
        } finally {
            setIsSending(false);
        }
    };

    // Clear the sending state after the send is complete/failed of if the recipient changes (we're viewing a different thread)
    useEffect(() => {
        if (isSendComplete || isSendFailed) {
            setIsSending(false);
        }
    }, [isSendComplete, isSendFailed, props.recipient.id, props.threadId, props.sessionId]);

    const containerSize = useResizeObserver(containerRef.current);
    const inputSize = useResizeObserver(inputRef.current);

    useEffect(() => {
        if (inputSize) {
            setInputHeight(inputSize[1]);
        }
    }, [inputSize]);

    useEffect(() => {
        if (containerSize) {
            setContainerHeight(containerSize[1]);
        }
    }, [containerSize]);

    // Set the height of the events container based on the height of the input container to ensure scrolling works correctly
    useEffect(() => {
        if (inputHeight && containerHeight) {
            setEventsContainerHeight(containerHeight - inputHeight);
        }
    }, [inputHeight, containerHeight]);

    const shouldShowTimestampPopover = Boolean(props.threadType !== ThreadType.EMAIL);

    return (
        <div id='content-container' ref={containerRef} className={styles['content-container']}>
            <div
                id='events-container'
                style={{height: eventsContainerHeight}}
                className={styles['events-container']}
            >
                <ChannelEvents
                    isLoading={props.isLoadingChannelEvents}
                    recipient={props.recipient}
                    channelEvents={props.isStartingNewThread ? [] : props.channelEvents}
                    fetchMoreRows={props.fetchMoreRows}
                    hasNextPage={props.hasNextPage}
                    onResendMessage={(messageId) =>
                        props.onResendMessage(messageId).then(() => {
                            props.fetchMostRecentChannelEvents();
                            props.refetchScheduledChannelEvents();
                        })
                    }
                    onDeleteMessage={(messageId) =>
                        props.onDeleteMessage(messageId).then(props.refetchScheduledChannelEvents)
                    }
                    isStartingNewThread={props.isStartingNewThread}
                    preventResend={
                        Boolean(props.cantSendStatus) || Boolean(props.shouldHideMessageInput)
                    }
                    sessionId={props.sessionId}
                    threadSummary={props.threadSummary}
                    shouldScrollToBottomOnInitialLoad={props.shouldScrollToBottomOnInitialLoad}
                    shouldShowTimestampPopover={shouldShowTimestampPopover}
                    isSidebar={props.isSidebar}
                    threadType={props.threadType}
                />
            </div>
            <div id='input-container' ref={inputRef} className={styles['message-input-container']}>
                {props.cantSendStatus !== undefined && !props.shouldHideMessageInput && (
                    // Important that we're checking for undefined here, a null value
                    // means they CAN send messages and a string value means they can't
                    <div className={styles['message-input-wrapper']}>
                        <MessageInput
                            sessionId={props.sessionId}
                            key={
                                props.threadId ||
                                props.sessionId ||
                                props.recipient.phoneNumber ||
                                props.recipient.id
                            }
                            onSend={handleSend}
                            onNoteCreate={handleNoteCreate}
                            isSending={isSending}
                            isComplete={props.isSendComplete || false}
                            cantSendMessage={
                                props.cantSendStatus && getCantSendMessage(props.cantSendStatus)
                            }
                            shouldHideControls={Boolean(
                                props.cantSendStatus && getShouldHideControls(props.cantSendStatus)
                            )}
                            showProgress={props.showProgress}
                            showSnoozeOptions={props.showSnoozeOptions}
                            recipient={props.recipient}
                            threadType={props.threadType}
                            sendErrorMessage={props.sendErrorMessage}
                            signature={props.signature}
                            userSendableEmailAddresses={props.userSendableEmailAddresses}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
